import type { AppLocale } from '@ielts-buddy/shared'
import { useMemo } from 'react'
import { t } from '../i18n/messages.js'

export type BandChartPoint = {
  completedAt: string
  overallBand: number
}

type BandProgressChartProps = {
  locale: AppLocale
  points: BandChartPoint[]
}

/** SVG band trend — no external chart deps; uses brand CSS variables via currentColor / fill. */
export function BandProgressChart({ locale, points }: BandProgressChartProps) {
  const model = useMemo(() => buildChartModel(points), [points])

  if (points.length === 0) {
    return null
  }

  if (points.length === 1) {
    const p = points[0]
    if (p == null) return null
    return (
      <div className="rounded-2xl border border-brand-border bg-brand-surface px-4 py-3">
        <p className="text-xs font-medium uppercase tracking-wider text-brand-text-muted">
          {t(locale, 'results.progressTitle')}
        </p>
        <p className="mt-2 text-sm text-brand-text-muted">
          {t(locale, 'results.progressHint')}
        </p>
        <p className="mt-3 text-2xl font-semibold tabular-nums text-brand-primary-strong">
          {p.overallBand}
          <span className="ml-2 text-sm font-medium text-brand-text-muted">
            {t(locale, 'results.band')}
          </span>
        </p>
      </div>
    )
  }

  const { pathLine, pathArea, dots, yTicks, xLabels } = model
  const ariaLabel = `${t(locale, 'results.progressTitle')}: ${points
    .map(
      (pt) =>
        `${new Date(pt.completedAt).toLocaleDateString()}: ${pt.overallBand}`,
    )
    .join(', ')}`

  return (
    <div className="rounded-2xl border border-brand-border bg-brand-surface p-3 sm:p-4">
      <div className="mb-2 flex flex-wrap items-end justify-between gap-2">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider text-brand-text-muted">
            {t(locale, 'results.progressTitle')}
          </p>
          <p className="text-[11px] text-brand-text-subtle mt-0.5">
            {t(locale, 'results.progressCaption')}
          </p>
        </div>
        <div className="text-right text-[11px] text-brand-text-muted tabular-nums">
          <span className="block">
            {t(locale, 'results.progressBest')}:{' '}
            <span className="font-semibold text-brand-primary-strong">
              {model.maxBand}
            </span>
          </span>
          <span className="block">
            {t(locale, 'results.progressSessions')}: {points.length}
          </span>
        </div>
      </div>

      <svg
        className="w-full h-[132px] text-brand-primary-strong"
        viewBox="0 0 320 132"
        role="img"
        aria-label={ariaLabel}
      >
        {yTicks.map((tick) => (
          <g key={tick.band}>
            <line
              x1="36"
              y1={tick.y}
              x2="308"
              y2={tick.y}
              className="stroke-brand-border/80"
              strokeWidth="1"
              strokeDasharray="3 4"
            />
            <text
              x="32"
              y={tick.y + 4}
              textAnchor="end"
              className="fill-brand-text-muted text-[9px] font-medium"
            >
              {tick.band}
            </text>
          </g>
        ))}

        <path
          d={pathArea}
          className="fill-brand-primary-soft/90"
          fillRule="evenodd"
        />
        <path
          d={pathLine}
          fill="none"
          className="stroke-brand-primary-strong"
          strokeWidth="2.25"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        {dots.map((d) => (
          <circle
            key={d.key}
            cx={d.cx}
            cy={d.cy}
            r="4.5"
            className="fill-brand-surface stroke-brand-primary-strong"
            strokeWidth="2"
          />
        ))}

        {xLabels.map((lab) => (
          <text
            key={lab.x}
            x={lab.x}
            y="126"
            textAnchor="middle"
            className="fill-brand-text-muted text-[9px]"
          >
            {lab.text}
          </text>
        ))}
      </svg>
    </div>
  )
}

function buildChartModel(points: BandChartPoint[]) {
  const bands = points.map((p) => p.overallBand)
  const minB = Math.max(0, Math.min(...bands) - 0.5)
  const maxB = Math.min(9, Math.max(...bands) + 0.5)
  const span = Math.max(maxB - minB, 0.5)

  const padL = 40
  const padR = 12
  const padT = 10
  const padB = 28
  const W = 320
  const H = 132
  const innerW = W - padL - padR
  const innerH = H - padT - padB

  const n = points.length
  const xAt = (i: number) =>
    padL + (n === 1 ? innerW / 2 : (innerW * i) / (n - 1))
  const yAt = (band: number) => padT + ((maxB - band) / span) * innerH

  const pts = points.map((p, i) => ({
    x: xAt(i),
    y: yAt(p.overallBand),
    band: p.overallBand,
  }))

  const pathLine = pts
    .map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`)
    .join(' ')
  const baseY = padT + innerH
  const pathArea = `${pathLine} L ${pts[pts.length - 1]?.x ?? 0} ${baseY} L ${pts[0]?.x ?? 0} ${baseY} Z`

  const dots = points.map((pt, i) => ({
    cx: pts[i]?.x ?? 0,
    cy: pts[i]?.y ?? 0,
    key: `${pt.completedAt}:${pt.overallBand}`,
  }))

  const tickBands: number[] = []
  const step = span <= 2 ? 0.5 : 1
  for (let b = Math.ceil(minB * 2) / 2; b <= maxB + 1e-6; b += step) {
    if (b >= minB - 1e-6 && b <= maxB + 1e-6) tickBands.push(b)
  }
  if (tickBands.length > 5) {
    tickBands.length = 0
    tickBands.push(minB, (minB + maxB) / 2, maxB)
  }
  if (tickBands.length === 0) {
    tickBands.push(minB, maxB)
  }
  const yTicks = tickBands.map((band) => ({
    band,
    y: yAt(band),
  }))

  const xLabels: { x: number; text: string }[] = []
  const pick = (i: number) => {
    const d = new Date(points[i]?.completedAt ?? '')
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
  }
  if (n <= 2) {
    xLabels.push({ x: xAt(0), text: pick(0) })
    if (n === 2) xLabels.push({ x: xAt(1), text: pick(1) })
  } else {
    xLabels.push({ x: xAt(0), text: pick(0) })
    xLabels.push({
      x: xAt(Math.floor((n - 1) / 2)),
      text: pick(Math.floor((n - 1) / 2)),
    })
    xLabels.push({ x: xAt(n - 1), text: pick(n - 1) })
  }

  return {
    pathLine,
    pathArea,
    dots,
    yTicks,
    xLabels,
    maxBand: Math.max(...bands),
  }
}
